/**
 * Service worker registration and update lifecycle.
 *
 * vite-plugin-pwa generates `sw.js` but registration is hand-rolled here so the
 * "new version waiting" signal can be handed to the silent update policy in
 * `appUpdate.ts`, which reloads at a moment that cannot interrupt the user.
 * The generated worker uses `skipWaiting: false` + `clientsClaim: true`, so a
 * waiting worker only takes over once the policy asks it to.
 */

/**
 * Fired on `window` when a new service worker is installed and waiting.
 * `appUpdate.ts` subscribes before registration runs, so the event is never
 * missed.
 */
export const SW_UPDATE_EVENT = 'homepoint:sw-update-available';

const SW_URL = '/sw.js';
const UPDATE_POLL_MS = 5 * 60 * 1000;

let waitingWorker: ServiceWorker | null = null;
let updateRequested = false;
let reloading = false;

const announceUpdate = (worker: ServiceWorker) => {
  waitingWorker = worker;
  window.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT));
};

const watchRegistration = (registration: ServiceWorkerRegistration) => {
  // A worker can already be waiting when the page loads (installed during a
  // previous visit that was never reloaded).
  if (registration.waiting && navigator.serviceWorker.controller) {
    announceUpdate(registration.waiting);
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;

    installing.addEventListener('statechange', () => {
      // Without an existing controller this is the very first install, which is
      // not an update the user needs to act on.
      if (
        installing.state === 'installed' &&
        navigator.serviceWorker.controller
      ) {
        announceUpdate(installing);
      }
    });
  });
};

/**
 * Activates the waiting worker and reloads into it. Safe to call when nothing
 * is waiting — it falls back to a plain reload.
 */
export const applyServiceWorkerUpdate = (): void => {
  updateRequested = true;

  if (waitingWorker) {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    // Safety net: if the new worker never claims this client, reload anyway
    // rather than leaving the tab on the old build.
    window.setTimeout(() => {
      if (!reloading) {
        reloading = true;
        window.location.reload();
      }
    }, 3000);
    return;
  }

  reloading = true;
  window.location.reload();
};

/**
 * Registers the service worker. No-op outside production builds: a dev-mode
 * worker would intercept fetches that Playwright's `page.route` stubs rely on.
 */
export const registerServiceWorker = (): void => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (!import.meta.env.PROD) return;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // clientsClaim also fires this on first install; only reload when the
    // update policy asked for the swap.
    if (!updateRequested || reloading) return;
    reloading = true;
    window.location.reload();
  });

  const register = async () => {
    try {
      const registration = await navigator.serviceWorker.register(SW_URL, {
        scope: '/',
      });

      watchRegistration(registration);

      const checkForUpdate = () => {
        registration.update().catch(() => {
          /* offline or transient — the next poll will retry */
        });
      };

      window.setInterval(checkForUpdate, UPDATE_POLL_MS);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) checkForUpdate();
      });
    } catch (error) {
      console.error('Service worker registration failed', error);
    }
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
};
